import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  Baby,
  BarChart3,
  BookOpen,
  Calculator,
  FileBadge,
  FileText,
  Home,
  LineChart,
  Link2,
  ListChecks,
  ListFilter,
  PiggyBank,
  Coins,
  Compass,
  CreditCard,
  Repeat,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  Scale,
  Settings,
  Sparkles,
  Target,
  TrendingDown,
  TrendingUp,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { flags } from '../../config/flags';
import { useNavState } from '../../hooks/useNavState';
import { useSidebarMode } from '../../hooks/useSidebarMode';
import ProfileMenu from './ProfileMenu';
import logoMark from '../../assets/clarity/logo-mark.svg';

export type SidebarItemId =
  | 'home'
  | 'review'
  | 'cash'
  | 'revenue'
  | 'expenses'
  | 'tax'
  | 'deductions'
  | 'plan'
  | 'budgets'
  | 'goals'
  | 'debts'
  | 'house'
  | 'kids'
  | 'retirement'
  | 'net-worth'
  | 'income-streams'
  | 'forecasting'
  | 'transactions'
  | 'invoices'
  | 'form-1099'
  | 'coa'
  | 'balance'
  | 'reports'
  | 'integrations'
  | 'rules'
  | 'settings';

interface NavItem {
  id: SidebarItemId;
  label: string;
  href: string;
  icon: LucideIcon;
}

interface NavGroup {
  section: string;
  items: NavItem[];
}

// Exported as the route source for the ⌘K command palette (A14) — a new nav
// entry appears in ⌘K for free.
// eslint-disable-next-line react-refresh/only-export-components
export const SIDEBAR_NAV: NavGroup[] = [
  // Clarity v4 app-redesign IA (#361): Money / Tax / Plan / Records (+ Setup).
  // Tax is its own pillar; Net Worth + Income Streams sit under Money; Records
  // (formerly Documents) holds the transaction record + Rules, with Chart of
  // Accounts / Balance Sheet as the accounting-advanced tail.
  {
    section: 'Money',
    items: [
      { id: 'home',     label: 'Home',     href: '/home',              icon: Home },
      // C2 · W3 — the keyboard-first Review queue. Flag-gated until B7.
      ...(flags.review
        ? [{ id: 'review' as const, label: 'Review', href: '/review', icon: ListChecks }]
        : []),
      { id: 'cash',           label: 'Cash',           href: '/cash',           icon: Wallet },
      { id: 'revenue',        label: 'Revenue',        href: '/revenue',        icon: TrendingUp },
      { id: 'expenses',       label: 'Expenses',       href: '/expenses',       icon: TrendingDown },
      { id: 'net-worth',      label: 'Net Worth',      href: '/net-worth',      icon: Coins },
      { id: 'income-streams', label: 'Income Streams', href: '/income-streams', icon: Repeat },
    ],
  },
  {
    section: 'Tax',
    items: [
      { id: 'tax',        label: 'Tax',        href: '/tax',        icon: Calculator },
      { id: 'deductions', label: 'Deductions', href: '/deductions', icon: Sparkles },
      { id: 'form-1099',  label: '1099s',      href: '/form-1099',  icon: FileBadge },
    ],
  },
  {
    section: 'Plan',
    items: [
      { id: 'plan',        label: 'Plan',        href: '/plan',        icon: Compass },
      { id: 'budgets',     label: 'Budgets',     href: '/budgets',     icon: Target },
      { id: 'goals',       label: 'Goals',       href: '/goals',       icon: Target },
      { id: 'debts',       label: 'Debts',       href: '/debts',       icon: CreditCard },
      { id: 'house',       label: 'House',       href: '/house',       icon: Home },
      { id: 'kids',        label: 'Kids',        href: '/kids',        icon: Baby },
      { id: 'retirement',  label: 'Retirement',  href: '/retirement',  icon: PiggyBank },
      { id: 'forecasting', label: 'Forecasting', href: '/forecasting', icon: LineChart },
    ],
  },
  {
    section: 'Records',
    items: [
      { id: 'transactions', label: 'Transactions',      href: '/transactions',  icon: Receipt },
      { id: 'invoices',     label: 'Invoices',          href: '/invoices',      icon: FileText },
      { id: 'reports',      label: 'Reports',           href: '/reports',       icon: BarChart3 },
      { id: 'rules',        label: 'Rules',             href: '/rules',         icon: ListFilter },
      { id: 'coa',          label: 'Chart of Accounts', href: '/accounts',      icon: BookOpen },
      { id: 'balance',      label: 'Balance Sheet',     href: '/balance-sheet', icon: Scale },
    ],
  },
  {
    section: 'Setup',
    items: [
      { id: 'integrations', label: 'Integrations', href: '/integrations',     icon: Link2 },
      { id: 'settings',     label: 'Settings',     href: '/settings',         icon: Settings },
    ],
  },
];

/**
 * C4 · W2/W5 — milestone phase per nav item (used only when
 * flags.progressiveNav is on). Items absent from this map are always visible
 * (phase 0). The Advanced trio (Chart of Accounts · Balance Sheet · Reports)
 * gets ADVANCED_PHASE so it never fronts — it surfaces only via "Show
 * everything" (or an explicit user pin), keeping the no-jargon promise (W5).
 */
const ADVANCED_PHASE = 99;
const PHASE_BY_ID: Partial<Record<SidebarItemId, number>> = {
  revenue: 1,
  expenses: 1,
  tax: 2,
  deductions: 2,
  // #361 — 1099s now lives in the Tax group; reveal it with its siblings
  // (was phase 0 under the old Documents group).
  'form-1099': 2,
  plan: 3,
  budgets: 3,
  goals: 3,
  debts: 3,
  house: 3,
  kids: 3,
  retirement: 3,
  'net-worth': 3,
  'income-streams': 3,
  forecasting: 3,
  coa: ADVANCED_PHASE,
  balance: ADVANCED_PHASE,
  reports: ADVANCED_PHASE,
};

/**
 * Matches the link's href against the current location, accounting for the
 * ?tab= query that several Money-tier items use until #222 splits them out.
 */
function useIsActive(href: string, activeId?: SidebarItemId, itemId?: SidebarItemId): boolean {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  if (activeId && itemId && activeId === itemId) return true;

  if (href.includes('?')) {
    const url = new URL(href, window.location.origin);
    const tab = url.searchParams.get('tab');
    return location.pathname === url.pathname && searchParams.get('tab') === tab;
  }
  if (href === '/home') {
    return (location.pathname === '/home' || location.pathname === '/') && !searchParams.get('tab');
  }
  return location.pathname === href;
}

interface NavLinkRowProps {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onNavigate?: () => void;
}

function NavLinkRow({ item, active, collapsed, onNavigate }: NavLinkRowProps) {
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? item.label : undefined}
      className={cn(
        'group relative flex items-center rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2',
        collapsed ? 'justify-center px-2 py-2' : 'gap-2.5 px-2.5 py-2',
        // #372 — prototype active treatment: left accent bar + tertiary wash
        // + forest ink (was a solid forest fill with inverse text).
        active
          ? 'bg-bg-tertiary text-forest font-semibold'
          : 'text-fg-primary font-medium hover:bg-bg-secondary',
      )}
    >
      {active && (
        <span
          aria-hidden="true"
          className="absolute inset-y-2 left-0 w-[3px] rounded-r-[3px] bg-forest"
        />
      )}
      <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} aria-hidden />
      {!collapsed && <span className="truncate text-small">{item.label}</span>}
    </Link>
  );
}

interface SidebarBodyProps {
  activeId?: SidebarItemId;
  collapsed: boolean;
  onNavigate?: () => void;
  showToggle?: boolean;
  toggleMode?: 'rail' | 'full';
  onToggleMode?: () => void;
}

function Logo({ collapsed }: { collapsed: boolean }) {
  return (
    <Link
      to="/home"
      aria-label="ForecastHQ home"
      className={cn(
        'flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2',
        collapsed ? 'justify-center px-1 py-1.5' : 'gap-2.5 px-2 py-1.5',
      )}
    >
      <img src={logoMark} alt="" className="h-7 w-7 flex-shrink-0" />
      {!collapsed && (
        <span className="text-base font-semibold leading-none tracking-tight text-fg-primary">
          Forecast<span className="text-terracotta">HQ</span>
        </span>
      )}
    </Link>
  );
}

function SidebarBody({
  activeId,
  collapsed,
  onNavigate,
  showToggle = false,
  toggleMode,
  onToggleMode,
}: SidebarBodyProps) {
  const nav = useNavState();

  // C4 — when progressive nav is on, reveal items by milestone phase. The
  // active item, pinned items, and "show everything" always win; hidden items
  // are user-curated out. Flag-off renders the full nav, byte-for-byte.
  const isVisible = (item: NavItem): boolean => {
    if (!flags.progressiveNav) return true;
    if (nav.hidden.has(item.id) && item.id !== activeId) return false;
    if (item.id === activeId) return true;
    if (nav.showAll || nav.pinned.has(item.id)) return true;
    const phase = PHASE_BY_ID[item.id] ?? 0;
    return phase <= nav.visiblePhase;
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-1.5">
      <div className="border-b border-border-subtle pb-3">
        <Logo collapsed={collapsed} />
      </div>

      <nav
        aria-label="Primary"
        className="flex-1 overflow-y-auto overflow-x-hidden py-2"
      >
        {SIDEBAR_NAV.map((group) => {
          const items = group.items.filter(isVisible);
          if (items.length === 0) return null;
          return (
            <div key={group.section} className="mb-2">
              {!collapsed && (
                <div className="fhq-micro px-2.5 pb-1 pt-2 text-fg-secondary">
                  {group.section}
                </div>
              )}
              <div className="flex flex-col gap-0.5">
                {items.map((item) => (
                  <NavLinkItem
                    key={item.id}
                    item={item}
                    activeId={activeId}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* C4 — "Show everything" escape hatch (W5). Nothing is ever
            permanently hidden by the phased reveal. */}
        {flags.progressiveNav && !collapsed && (
          <button
            type="button"
            onClick={() => nav.setShowAll(!nav.showAll)}
            aria-pressed={nav.showAll}
            className="mt-1 flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-small font-medium text-fg-secondary transition-colors hover:bg-bg-secondary hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest"
          >
            {nav.showAll ? 'Show less' : 'Show everything'}
          </button>
        )}
      </nav>

      <div className="border-t border-border-subtle pt-2">
        <ProfileMenu collapsed={collapsed} />
      </div>

      {showToggle && (
        <button
          type="button"
          onClick={onToggleMode}
          aria-label={toggleMode === 'rail' ? 'Expand sidebar' : 'Collapse sidebar to rail'}
          className={cn(
            'mt-1 inline-flex items-center rounded-md text-fg-secondary transition-colors hover:bg-bg-secondary hover:text-fg-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2',
            collapsed ? 'justify-center px-2 py-2' : 'gap-2 px-2.5 py-2',
          )}
        >
          {toggleMode === 'rail' ? (
            <PanelLeftOpen className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} aria-hidden />
          ) : (
            <PanelLeftClose className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} aria-hidden />
          )}
          {!collapsed && <span className="text-small">Collapse</span>}
        </button>
      )}
    </div>
  );
}

function NavLinkItem({
  item,
  activeId,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  activeId?: SidebarItemId;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const active = useIsActive(item.href, activeId, item.id);
  return <NavLinkRow item={item} active={active} collapsed={collapsed} onNavigate={onNavigate} />;
}

interface DesktopSidebarProps {
  active?: SidebarItemId;
}

function DesktopSidebar({ active }: DesktopSidebarProps) {
  const { mode, toggle } = useSidebarMode();
  const [hovering, setHovering] = useState(false);

  const isRail = mode === 'rail';
  const showOverlay = isRail && hovering;

  return (
    <div
      className="relative hidden sm:block"
      onPointerEnter={() => isRail && setHovering(true)}
      onPointerLeave={() => isRail && setHovering(false)}
    >
      <aside
        className={cn(
          'sticky top-0 flex h-screen flex-shrink-0 flex-col border-r border-border-subtle bg-bg-primary px-2 py-3 transition-[width] duration-200 ease-out',
          isRail ? 'w-[64px]' : 'w-[240px]',
        )}
        aria-hidden={showOverlay}
      >
        <SidebarBody
          activeId={active}
          collapsed={isRail}
          showToggle
          toggleMode={mode}
          onToggleMode={toggle}
        />
      </aside>

      {showOverlay && (
        <aside
          className="fixed left-0 top-0 z-nav flex h-screen w-[240px] flex-col border-r border-border bg-bg-primary px-2 py-3 shadow-md"
          onPointerEnter={() => setHovering(true)}
          onPointerLeave={() => setHovering(false)}
        >
          <SidebarBody
            activeId={active}
            collapsed={false}
            showToggle
            toggleMode={mode}
            onToggleMode={toggle}
          />
        </aside>
      )}
    </div>
  );
}

interface MobileDrawerProps {
  active?: SidebarItemId;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function MobileDrawer({ active, open, onOpenChange }: MobileDrawerProps) {
  return (
    <Drawer open={open} onClose={() => onOpenChange(false)}>
      <SidebarBody
        activeId={active}
        collapsed={false}
        onNavigate={() => onOpenChange(false)}
      />
    </Drawer>
  );
}

interface DrawerProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}

function Drawer({ open, onClose, children }: DrawerProps) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-modal sm:hidden" role="dialog" aria-modal="true" aria-label="Navigation">
      <div
        className="absolute inset-0 bg-ink/40"
        onClick={onClose}
        aria-hidden
      />
      <div className="absolute left-0 top-0 flex h-full w-[280px] flex-col border-r border-border bg-bg-primary px-2 py-3 shadow-lg">
        {children}
      </div>
    </div>
  );
}

interface AppSidebarRootProps {
  active?: SidebarItemId;
  mobileOpen: boolean;
  onMobileOpenChange: (open: boolean) => void;
}

export default function AppSidebar({ active, mobileOpen, onMobileOpenChange }: AppSidebarRootProps) {
  return (
    <>
      <DesktopSidebar active={active} />
      <MobileDrawer active={active} open={mobileOpen} onOpenChange={onMobileOpenChange} />
    </>
  );
}
